import type { CSSProperties } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowUp,
  Clock,
  Copy,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  SkipForward,
  Sparkles,
  Trash2,
  Users,
  Variable,
} from "lucide-react";
import type { CampaignDetail, CampaignStep } from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/email/EmailBadges";
import { TemplateText } from "@/components/email/TemplateText";
import { delayLabel, plural, stepDays } from "@/lib/email";
import { cn } from "@/lib/utils";

export interface StepBoardProps {
  campaign: CampaignDetail;
  /** The user manages campaigns of this project. */
  canEdit: boolean;
  onEdit: (step: CampaignStep) => void;
  /** Editorial navigation only; never grants sequence mutation. */
  onPilotVariant?: (variant: "A" | "B") => void;
  onAdd: (position?: number) => void;
  onDuplicate: (step: CampaignStep) => void;
  onDelete: (step: CampaignStep) => void;
  onReorder: (stepIds: number[]) => void;
}

function useStepSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

function reorder(steps: CampaignStep[], event: DragEndEvent): number[] | null {
  const { active, over } = event;
  if (!over || active.id === over.id) return null;
  const from = steps.findIndex((s) => s.id === active.id);
  const to = steps.findIndex((s) => s.id === over.id);
  if (from < 0 || to < 0) return null;
  return arrayMove(steps, from, to).map((s) => s.id);
}

// ── shared card content (Steps view and Canvas) ─────────────────────────

/** Why an action is off, under its label in a menu: a disabled item shows no
 *  tooltip. */
function Because({ reason }: { reason: string | null | undefined }) {
  if (!reason) return null;
  return <span className="block text-[11px] font-normal leading-snug text-slate-400">{reason}</span>;
}

/** A step's actions, as the campaign's sequence allows them now. `move` adds
 *  Move up / Move down where dragging is not the way to reorder (the Canvas);
 *  null disables the direction. */
export function StepMenu({
  step,
  props,
  move,
}: {
  step: CampaignStep;
  props: StepBoardProps;
  move?: { up: (() => void) | null; down: (() => void) | null };
}) {
  if (!props.canEdit) return null;
  const sequence = props.campaign.sequence;
  const caps = step.capabilities;
  const canDelete = caps?.can_delete ?? true;
  const last = step.position === props.campaign.steps.length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-slate-400"
          aria-label={`Actions for step ${step.position}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => props.onEdit(step)}>
          <Pencil className="h-4 w-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!sequence.can_add_step} onClick={() => props.onDuplicate(step)}>
          <Copy className="h-4 w-4" />
          <span>
            {sequence.active_sequence && !last ? "Duplicate to the end" : "Duplicate"}
            <Because reason={sequence.can_add_step ? null : sequence.reasons.can_add_step} />
          </span>
        </DropdownMenuItem>
        {move && (
          <>
            <DropdownMenuItem disabled={!move.up} onClick={() => move.up?.()}>
              <ArrowUp className="h-4 w-4" /> Move up
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!move.down} onClick={() => move.down?.()}>
              <ArrowDown className="h-4 w-4" /> Move down
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!canDelete}
          className="items-start text-rose-600 focus:text-rose-700"
          onClick={() => props.onDelete(step)}
        >
          <Trash2 className="mt-0.5 h-4 w-4" />
          <span>
            Delete
            <Because reason={canDelete ? null : caps?.delete_reason} />
          </span>
        </DropdownMenuItem>
        {caps && !canDelete && caps.skip_reason && (
          <DropdownMenuItem disabled className="items-start">
            <SkipForward className="mt-0.5 h-4 w-4" />
            <span>
              Skip step
              <Because reason={caps.skip_reason} />
            </span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Where a step stands in an active sequence: added after launch, not in
 *  Lemlist yet. */
export function StepBadges({ step }: { step: CampaignStep }) {
  const caps = step.capabilities;
  if (!caps) return null;
  return (
    <>
      {caps.added_after_launch && (
        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-violet-100">
          Added after launch
        </span>
      )}
      {caps.pending && (
        <Hint content="Not in Lemlist yet: the next sync adds it at the end of the sequence, with each running contact's own email.">
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-100">
            Not in Lemlist yet
          </span>
        </Hint>
      )}
    </>
  );
}

export function StepFacts({ step, contacts, synced }: { step: CampaignStep; contacts: number; synced: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-slate-500">
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5">
        <Variable className="h-3 w-3" />
        {plural(step.variables_count, "variable")}
      </span>
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5",
          step.ai_variables.length ? "bg-fuchsia-50 text-fuchsia-700" : "bg-slate-100",
        )}
      >
        <Sparkles className="h-3 w-3" />
        {plural(step.ai_variables.length, "AI variable")}
      </span>
      {contacts > 0 && (
        <Hint content="Running contacts whose last email was this step">
          <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-indigo-700">
            <Users className="h-3 w-3" />
            {contacts}
          </span>
        </Hint>
      )}
      <span className="ml-auto inline-flex items-center gap-1 text-slate-400">
        {synced ? (
          step.unsynced ? (
            <Hint content="This content has not reached Lemlist yet: the next sync sends it.">
              <span className="inline-flex items-center gap-1 text-amber-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                Unsynced
              </span>
            </Hint>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Synced
            </span>
          )
        ) : null}
        <span>v{step.version}</span>
      </span>
    </div>
  );
}

export function SubjectLine({ step }: { step: CampaignStep }) {
  if (!step.subject_template.trim()) {
    return step.position === 1 ? (
      <span className="text-sm font-medium italic text-rose-500">No subject — the first email needs one</span>
    ) : (
      <span className="text-sm italic text-slate-400">No subject — sent as a reply in the same thread</span>
    );
  }
  return <TemplateText template={step.subject_template} className="text-sm font-medium text-slate-900" />;
}

// ── Steps view ───────────────────────────────────────────────────────────

export function StepsView(props: StepBoardProps) {
  const { campaign } = props;
  const steps = campaign.steps;
  const days = stepDays(steps);
  const sensors = useStepSensors();
  const sequence = campaign.sequence;
  const draggable = props.canEdit && sequence.can_reorder_steps && steps.length > 1;

  return (
    <div className="mx-auto max-w-3xl">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const ids = reorder(steps, event);
          if (ids) props.onReorder(ids);
        }}
      >
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <ol>
            {steps.map((step, index) => (
              <li key={step.id}>
                {index > 0 && (
                  <div className="flex items-center gap-3 py-1.5 pl-[22px]">
                    <span className="h-6 w-px bg-gradient-to-b from-slate-200 to-slate-300" />
                    <button
                      type="button"
                      disabled={!props.canEdit}
                      onClick={() => props.onEdit(step)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-500 shadow-sm-tile transition-colors enabled:hover:border-indigo-200 enabled:hover:text-indigo-600"
                    >
                      <ArrowDown className="h-3 w-3" />
                      {step.delay_value === 0 ? "same day" : `${delayLabel(step.delay_value, step.delay_unit)} later`}
                    </button>
                  </div>
                )}
                <SortableStepRow
                  step={step}
                  day={days[index]}
                  draggable={draggable}
                  props={props}
                  contacts={campaign.contacts_by_step[String(step.position)] ?? 0}
                  synced={campaign.provider_mode !== "NONE"}
                />
              </li>
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      {props.canEdit && (
        <div className={cn("pl-[22px]", steps.length && "pt-3")}>
          {steps.length > 0 && <span className="ml-0 block h-4 w-px bg-slate-200" />}
          <Hint content={sequence.can_add_step ? null : sequence.reasons.can_add_step}>
            <span className="inline-block">
              <button
                type="button"
                disabled={!sequence.can_add_step}
                onClick={() => props.onAdd()}
                className="-ml-[22px] mt-1 flex w-[calc(100%+22px)] min-w-[280px] items-center justify-center gap-2 rounded-plane border-2 border-dashed border-slate-200 px-4 py-4 text-sm font-medium text-slate-500 transition-colors enabled:hover:border-indigo-300 enabled:hover:bg-indigo-50/40 enabled:hover:text-indigo-600 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {steps.length ? "Add a follow-up" : "Add the first email"}
              </button>
            </span>
          </Hint>
        </div>
      )}
    </div>
  );
}

function SortableStepRow({
  step,
  day,
  draggable,
  props,
  contacts,
  synced,
}: {
  step: CampaignStep;
  day: number;
  draggable: boolean;
  props: StepBoardProps;
  contacts: number;
  synced: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
    disabled: !draggable,
  });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={cn("relative flex gap-3", isDragging && "z-20")}>
      <div className="flex w-11 shrink-0 flex-col items-center pt-4">
        <span
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold",
            step.position === 1 ? "bg-indigo-600 text-white shadow-cta" : "bg-white text-indigo-600 ring-1 ring-indigo-100",
          )}
        >
          {step.position}
        </span>
      </div>
      <article
        onClick={() => props.canEdit && props.onEdit(step)}
        className={cn(
          "group min-w-0 flex-1 rounded-plane bg-white p-4 shadow-card-rest transition-all",
          props.canEdit && "cursor-pointer hover:shadow-plane-lift",
          isDragging && "shadow-elevated ring-2 ring-indigo-200",
        )}
      >
        <header className="flex items-start gap-2">
          {draggable ? (
            <button
              type="button"
              className="mt-0.5 cursor-grab touch-none rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500 active:cursor-grabbing"
              aria-label={`Move step ${step.position}`}
              onClick={(e) => e.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          ) : !props.campaign.sequence.can_reorder_steps && props.canEdit ? (
            <Hint content={props.campaign.sequence.reasons.can_reorder_steps ?? null}>
              <span className="mt-0.5 p-0.5 text-slate-200">
                <GripVertical className="h-4 w-4" />
              </span>
            </Hint>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-[15px] font-semibold text-slate-900">{step.name}</h3>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                <Clock className="h-3 w-3" />
                Day {day}
              </span>
              <StepBadges step={step} />
            </div>
            <div className="mt-2 truncate">
              <SubjectLine step={step} />
            </div>
            <p className="mt-1 line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-slate-600">
              <TemplateText template={step.body_template} empty="Empty body" />
            </p>
          </div>
          <StepMenu step={step} props={props} />
        </header>
        <div className="mt-3 border-t border-slate-100 pt-2.5">
          <StepFacts step={step} contacts={contacts} synced={synced} />
        </div>
      </article>
    </div>
  );
}
